import { Typography } from "@mui/material";
import useUserStore from "../state-management/useUserStore";
import { CustomizedTooltip } from "./appHelpers";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { PATHS } from "../routes/routes.paths";

export const PERMISSIONS = {
    // Employees
    EMPLOYEES_CREATE: "employees:create",
    EMPLOYEES_WRITE: "employees:write", 
    EMPLOYEES_READ: "employees:read",
    EMPLOYEES_TIME_WRITE: "employees:time:write",	

    // Payroll & Payments
    PAYSLIPS_WRITE: "payslips:write",
    PAYSLIPS_READ: "payslips:read",
    PAYROLL_WRITE: "payroll:write",
    PAYMENTS_READ: "payments:read",
    PAYMENTS_WRITE: "payments:write",
    SALARY_READ: "salary:read",
    SALARY_WRITE: "salary:write",
	SALARY_CREATE: "employees:create",

    // Organization
    ORGANIZATION_ROLES_WRITE: "organization_roles:write",
    ORGANIZATION_ROLES_READ: "organization_roles:read",
    ORGANIZATIONS_READ: "organizations:read",
    ORGANIZATIONS_WRITE: "organizations:write",

    // Companies
    COMPANIES_READ: "companies:read",
    COMPANIES_WRITE: "companies:write",

    // Company Configs
    COMPANY_CONFIGS_WRITE: "company_configs:write",
    COMPANY_CONFIGS_READ: "company_configs:read",
    COMPANY_CONFIGS_CONDUCT_WRITE: "company_configs:conduct:write",
    COMPANY_CONFIGS_HIERARCHY_WRITE: "company_configs:hierarchy:write",
    COMPANY_CONFIGS_INFORMATION_WRITE: "company_configs:information:write",
    COMPANY_CONFIGS_LEAVE_WRITE: "company_configs:leave:write",

    // Platform Configs
    PLATFORM_CONFIGS_STATUTORY_READ: "platform_configs:statutory:read",

    // Users
    USERS_WRITE: "users:write",
    USERS_READ: "users:read",
    USERS_PASSWORD_WRITE: "users:password:write",

    // Password Policies
    PASSWORD_POLICIES_WRITE: "password_policies:write",
    PASSWORD_POLICIES_READ: "password_policies:read",

    // Subscriptions
    SUBSCRIPTIONS_WRITE: "subscriptions:write",
    SUBSCRIPTIONS_READ: "subscriptions:read",

    // Reports
    REPORTS_BILLING_WRITE: "reports:billing:write",
    REPORTS_BILLING_READ: "reports:billing:read",
    REPORTS_EMPLOYEE_READ: "reports:employee:read",

    // Org Configs
    ORG_CONFIGS_COMPANY_WRITE: "org_configs:company:write",
    ORG_CONFIGS_COMPANY_READ: "org_configs:company:read",

    // Loans
    LOANS_WRITE: "loans:write",
    LOANS_READ: "loans:read",

    // Announcements
    ANNOUNCEMENTS_WRITE: "announcements:write",

    // Audit & Permissions
    AUDIT_LOGS_READ: "audit_logs:read",
    PERMISSIONS_READ: "permissions:read"
}

/**
 * Custom hook that provides utility functions to check user permissions
 * @returns Object containing two permission checking functions:
 * - checkUserPermissionsAnd: Checks if user has ALL specified permissions
 * - checkUserPermissionsOr: Checks if user has ANY of the specified permissions
 * 
 * @example
 * ```tsx
 * const { checkUserPermissionsAnd, checkUserPermissionsOr } = useCheckUserPermissions();
 * 
 * // Check if user has all permissions
 * const hasAllPermissions = checkUserPermissionsAnd(['READ', 'WRITE']);
 * 
 * // Check if user has any of the permissions
 * const hasAnyPermission = checkUserPermissionsOr(['READ', 'WRITE']);
 * ```
 */
export function useCheckUserPermissions() {
	const { currentUser } = useUserStore();
	const userOrganizationRole = currentUser.user.organizationRole;

	/**
	 * Checks if the user has all specified permissions.
	 * Uses AND logic - returns true only if user has ALL of the provided permissions.
	 * @param permissions - Array of permission strings to check against user's permissions
	 * @returns {boolean} True if user has all specified permissions, false otherwise
	 */
	const checkUserPermissionsAnd = (permissions: string[]) => permissions.every((permission) =>
		userOrganizationRole?.permissions.includes(permission)
	);

	/**
	 * Checks if the user has at least one of the specified permissions.
	 * @param permissions - Array of permission strings to check against user's permissions
	 * @returns boolean - Returns true if user has at least one of the specified permissions, false otherwise
	 * @example
	 * checkUserPermissionsOr(['VIEW_USERS', 'EDIT_USERS']); // returns true if user has either permission
	 */
	const checkUserPermissionsOr = (permissions: string[]) => permissions.some((permission) =>
		userOrganizationRole?.permissions.includes(permission)
	);
	
    return { checkUserPermissionsAnd, checkUserPermissionsOr };
}

/**
 * Custom hook to validate user permissions and handle navigation based on user type.
 * If the user doesn't have the required permissions, they will be redirected to a type-specific forbidden page.
 * 
 * @param permissions - Array of permission strings to check against user's permissions
 * @param permissionRule - Rule to apply when checking multiple permissions
 *                        "AND": User must have all permissions
 *                        "OR": User must have at least one permission
 *                        (default: "AND")
 * @param navigatePath - Optional custom path to redirect to when user lacks permissions
 *                      If not provided, redirects to type-specific forbidden pages:
 *                      - Admin: PATHS.ADMIN_FORBIDDEN
 *                      - Payroll Company: PATHS.PC_FORBIDDEN
 *                      - HR: PATHS.HR_FORBIDDEN
 *                      - Employee: PATHS.SELF_SERVICE_FORBIDDEN
 * @returns Object containing hasPermission boolean indicating if user has required permissions
 * 
 * @example
 * ```tsx
 * const hasPermission = useValidateUserPermissions(['READ_USERS'], 'AND');
 * // Or with custom navigation path
 * useValidateUserPermissions(['WRITE_USERS'], 'OR', '/custom-forbidden');
 * ```
 */
export function useValidateUserPermissions(
	permissions: string[], 
	permissionRule: "OR" | "AND" = "AND",
	navigatePath?: string,
) {
	const navigate = useNavigate();
	const { userType } = useUserStore();
	const { checkUserPermissionsAnd, checkUserPermissionsOr } = useCheckUserPermissions();
	const hasPermission = permissionRule === "OR" 
		? checkUserPermissionsOr(permissions) 
		: checkUserPermissionsAnd(permissions);
		
	useEffect(() => {
		if (!hasPermission) {
			switch (userType) {
				case "ADMIN":
					navigate(navigatePath || PATHS.ADMIN_FORBIDDEN);
					break;
				case "ORGANIZATION":
					navigate(navigatePath || PATHS.PC_FORBIDDEN);
					break;
				case "HR":
					navigate(navigatePath || PATHS.HR_FORBIDDEN);
					break;
				default:
					navigate(navigatePath || PATHS.SELF_SERVICE_FORBIDDEN);
			}
		}
	}, [hasPermission])

	return hasPermission;
}

type PermissionCheckerProps = {
	permissions: string[];
	permissionRule?: "OR" | "AND";
	children: React.ReactNode;
	message?: string;
	placement?:
		| 'bottom-end'
		| 'bottom-start'
		| 'bottom'
		| 'left-end'
		| 'left-start'
		| 'left'
		| 'right-end'
		| 'right-start'
		| 'right'
		| 'top-end'
		| 'top-start'
		| 'top';
}

/**
 * A component that conditionally renders its children based on user permissions.
 * If the user lacks required permissions, it displays a tooltip with a custom message.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {string[]} props.permissions - Array of permission strings to check against
 * @param {'AND' | 'OR'} props.permissionRule - Rule for permission checking:
 *   - 'AND': User must have all permissions
 *   - 'OR': User must have at least one permission
 * @param {React.ReactNode} props.children - Content to render if permissions are granted
 * @param {string} [props.message] - Custom message to display in tooltip when access is denied
 * @param {string} [props.placement] - Tooltip placement position (default: 'top')
 * 
 * @returns {JSX.Element} A component that either renders the children if permitted,
 * or a disabled button with tooltip if access is denied
 * 
 * @example
 * <PermissionChecker
 *   permissions={['VIEW_USERS', 'EDIT_USERS']}
 *   permissionRule="AND"
 *   message="No access to user management"
 * >
 *   <UserManagementPanel />
 * </PermissionChecker>
 */
export const PermissionChecker = ({ 
	permissions,
	permissionRule, 
	children, 
	message, 
	placement 
}: PermissionCheckerProps) => {
	const { checkUserPermissionsAnd, checkUserPermissionsOr } = useCheckUserPermissions();
	const hasPermission = permissionRule === "OR" 
		? checkUserPermissionsOr(permissions) 
		: checkUserPermissionsAnd(permissions);

	return (
		<>
			{hasPermission 
				? children 
				: <CustomizedTooltip 
					title={message || "You are not authorized to access this feature"} 
					placement={placement || "top"} 
					arrow
				>
                    <Typography> 
                        <button type="button" className="w-fit h-fit cursor-not-allowed failed-permission-check" disabled>
							{children}
						</button>
                    </Typography>
                </CustomizedTooltip>
			}
		</>
	);
};